/**
 * №13.1 (design-pack §5) — the **resource** identity picker.
 *
 * Renders the `resource` field on time capture as a picker over workspace
 * **members + agents** (grouped "People" / "Agents"), not a free-text box (M24).
 * The stored value is the member/agent **id** — an interim identity ref (M25/N13:
 * the engine field stays `text`, it just holds an id). The trigger shows the
 * resolved display name (M26). Controlled: `value` is the selected id (or null).
 *
 * Loading reuses MemberPicker's pattern — members via `useWorkspaceMembers`,
 * agents via `useListTree(type: "agent")`. This is the identity picker only; the
 * resource→rate linkage lands with the rates spec (N12), not here.
 */
import { useState, useRef, useEffect, useMemo, type CSSProperties } from "react";
import { Avatar } from "../primitives";
import type { Persona } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { useWorkspaceMembers } from "../../hooks/use-workspace-members";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";

interface ResourcePickerProps {
  /** Selected identity — a member user_id, an agent node_id, or null. */
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
}

interface Option {
  id: string;
  label: string;
  sublabel?: string;
  group: "People" | "Agents";
  persona: Persona;
  isYou?: boolean;
}

export function ResourcePicker({ value, onChange, placeholder = "Select a resource…" }: ResourcePickerProps) {
  const { user, currentWorkspaceId } = useAuth();
  const members = useWorkspaceMembers();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const options = useMemo<Option[]>(() => {
    const out: Option[] = [];
    const memberOpts: Option[] = (members.data?.members ?? [])
      .filter((m) => m.status !== "pending")
      .map((m) => ({
        id: m.id,
        label: m.name || m.email,
        sublabel: m.name ? m.email : undefined,
        group: "People" as const,
        persona: "gray" as Persona,
        isYou: m.id === user?.id,
      }));
    // Current user floats to the top of People with a (You) suffix.
    memberOpts.sort((a, b) => (a.isYou === b.isYou ? a.label.localeCompare(b.label) : a.isYou ? -1 : 1));
    out.push(...memberOpts);

    const agentOpts: Option[] = (tree.data ?? [])
      .filter((n) => n.type === "agent")
      .map((n) => ({
        id: n.id,
        label: n.name,
        sublabel: "Agent",
        group: "Agents" as const,
        persona: getAgentPersona(n),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    out.push(...agentOpts);
    return out;
  }, [members.data, tree.data, user?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.sublabel?.toLowerCase().includes(q),
    );
  }, [options, query]);

  const groups = useMemo(() => {
    const people = filtered.filter((o) => o.group === "People");
    const agents = filtered.filter((o) => o.group === "Agents");
    return [
      { name: "People" as const, rows: people },
      { name: "Agents" as const, rows: agents },
    ].filter((g) => g.rows.length > 0);
  }, [filtered]);

  const selected = value === null ? null : options.find((o) => o.id === value);

  function choose(id: string) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  const triggerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    fontSize: 13,
    padding: "7px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    textAlign: "left",
  };

  const dropdownStyle: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: 260,
    overflowY: "auto",
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 200,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  };

  const searchStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "6px 8px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    marginBottom: 4,
    fontFamily: "var(--font-sans)",
  };

  const groupLabelStyle: CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "var(--color-text-tertiary)",
    padding: "6px 8px 2px",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" style={triggerStyle} onClick={() => setOpen((v) => !v)}>
        {selected ? (
          <>
            <Avatar persona={selected.persona} size="xs" name={selected.label} />
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.label}{selected.isYou ? " (You)" : ""}
            </span>
          </>
        ) : (
          <span style={{ flex: 1, color: "var(--color-text-tertiary)" }}>{placeholder}</span>
        )}
        <i className="ti ti-selector" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
      </button>

      {open && (
        <div style={dropdownStyle}>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people or agents…"
            style={searchStyle}
          />
          {groups.map((g) => (
            <div key={g.name}>
              <div style={groupLabelStyle}>{g.name}</div>
              {g.rows.map((o) => (
                <Row key={`${o.group}:${o.id}`} option={o} selected={o.id === value} onClick={() => choose(o.id)} />
              ))}
            </div>
          ))}
          {groups.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "8px 10px" }}>
              No matches.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ option, selected, onClick }: { option: Option; selected: boolean; onClick: () => void }) {
  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "6px 8px",
    border: "none",
    background: selected ? "var(--color-background-secondary)" : "transparent",
    borderRadius: "var(--border-radius-sm)",
    cursor: "pointer",
    fontSize: 13,
    textAlign: "left",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
  };
  return (
    <button type="button" style={rowStyle} onClick={onClick}>
      <Avatar persona={option.persona} size="xs" name={option.label} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {option.label}{option.isYou ? " (You)" : ""}
        {option.sublabel && (
          <span style={{ color: "var(--color-text-tertiary)", fontSize: 11, marginLeft: 6 }}>{option.sublabel}</span>
        )}
      </span>
      {selected && <i className="ti ti-check" style={{ fontSize: 14, color: "var(--color-text-info)" }} aria-hidden="true" />}
    </button>
  );
}
